import { useContext, useEffect, useRef, useState } from 'react'
import { getLS } from '../../../helper/LocalStorage'
import { NavLink, useLocation, useParams } from 'react-router-dom'
import DataTableComponent from './DataTableSearch'
import allColumns from './allColumns.json'
import ContentPage, {
	BottomPage,
	HeaderPage,
} from '../../../components/Tabs/ContentPage'
import { PageContext } from './context'
import { SystemContext, UserContext } from '../../../shared/context'
import { datatableSort } from '../../../helper/Sort'
import SimulateTable from '../../../shared/SimulateTable/SimulateTable'
import EmptyTable from '../../../shared/EmptyTable'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/pro-solid-svg-icons'
import { HeaderButtons } from '../../../components/Tabs/HeaderButtons'
import {
	addUiBreadcrumb,
	captureFeatureException,
	captureFeatureMessage,
	getDomSnapshot,
	getNavigationSnapshot,
	setSentryFeatureContext,
} from '../../../helper/SentryMonitoring'

const EMPTY_ARRAY = []

const getObjectValueByPath = (obj, path) => {
	const keys = path.split('.')
	let result = obj
	for (const key of keys) {
		if (result == null) return undefined
		result = result[key]
	}
	return result
}

const allColumnsWithSelector = allColumns.map(col => {
	const thisColumn = { ...col }
	thisColumn.cell = cell => (
		<NavLink
			to={'/tenant/' + cell.id}
			className="flex items-center justify-start w-full h-full"
		>
			{getObjectValueByPath(cell, thisColumn.pathSelector)}
		</NavLink>
	)
	if (thisColumn.sortable) {
		thisColumn.sortFunction = (rowA, rowB) =>
			datatableSort(
				[rowA, rowB],
				x => getObjectValueByPath(x, thisColumn.pathSelector),
				'asc'
			)[0] === rowA
				? -1
				: 1
	}
	return thisColumn
})

const filteredData = (filterText, tenants) => {
	if (tenants?.length > 0) {
		const propertiesToFilter = allColumnsWithSelector
			.map(col => col.id)
			.filter(col => col)

		const filtered = tenants.filter(tenant => {
			for (let property of propertiesToFilter) {
				let myItemProperty =
					tenant[property] ?? tenant.configuration?.attributes?.[property] ?? ''

				if (typeof myItemProperty === 'object' && myItemProperty !== null) {
					if (myItemProperty.name) {
						myItemProperty = myItemProperty.name
					} else {
						return false
					}
				}

				if (
					typeof myItemProperty === 'string' &&
					myItemProperty.toLowerCase().includes(filterText.toLowerCase())
				) {
					return true
				}
			}
			return false
		})
		return filtered
	}
}

const Search = () => {
	const { systemJson } = useContext(SystemContext)
	const { profile } = useContext(UserContext)
	const { tenants, searchDiagnostics } = useContext(PageContext)
	const location = useLocation()
	const params = useParams()
	const [columns, setColumns] = useState([])
	const [filterText, setFilterText] = useState('')
	const [resetPaginationToggle, setResetPaginationToggle] = useState(false)
	const [dataTableData, setDataTableData] = useState(null)
	const hasLoggedFilterInit = useRef(false)
	const hasReportedIncompleteSystemJson = useRef(false)
	const tenantId = params.tenantId || null
	const forbiddenTenantIds = Array.isArray(systemJson?.forbiddenTenantsInSearch)
		? systemJson.forbiddenTenantsInSearch
		: EMPTY_ARRAY
	const searchRoute = `${location.pathname}${location.search}`

	useEffect(() => {
		setSentryFeatureContext({
			feature: 'search',
			tenantId,
			path: location.pathname,
			extra: {
				route: 'search_page',
				searchRoute,
				userId: profile?.id,
				username: profile?.username,
				hasTenantsLoaded: Array.isArray(tenants),
				tenantCount: Array.isArray(tenants) ? tenants.length : null,
				searchDiagnostics,
				dom: getDomSnapshot(),
				lastNavigation: getNavigationSnapshot(),
			},
		})

		addUiBreadcrumb({
			category: 'search',
			message: 'search_page_mount',
			data: {
				currentPath: location.pathname,
				searchRoute,
				tenantId,
				userId: profile?.id,
				username: profile?.username,
				hasTenantsLoaded: Array.isArray(tenants),
				tenantCount: Array.isArray(tenants) ? tenants.length : null,
				searchDiagnostics,
				dom: getDomSnapshot(),
				lastNavigation: getNavigationSnapshot(),
			},
		})
	}, [
		location.pathname,
		location.search,
		profile?.id,
		profile?.username,
		searchDiagnostics,
		searchRoute,
		tenantId,
		tenants,
	])

	useEffect(() => {
		if (
			Array.isArray(systemJson?.forbiddenTenantsInSearch) ||
			hasReportedIncompleteSystemJson.current
		) {
			return
		}

		hasReportedIncompleteSystemJson.current = true
		captureFeatureMessage('Search mounted with incomplete systemJson', {
			feature: 'search',
			location: 'Search.mount',
			level: 'warning',
			extra: {
				currentPath: location.pathname,
				searchRoute,
				tenantId,
				userId: profile?.id,
				username: profile?.username,
				systemJsonLoaded: !!systemJson,
				searchDiagnostics,
				dom: getDomSnapshot(),
				lastNavigation: getNavigationSnapshot(),
			},
			tags: {
				severity: 'warning',
			},
		})
	}, [
		location.pathname,
		location.search,
		profile?.id,
		profile?.username,
		searchDiagnostics,
		searchRoute,
		systemJson,
		tenantId,
	])

	useEffect(() => {
		const navigation = getNavigationSnapshot()

		if (
			navigation?.fromFeature !== 'home' ||
			navigation?.toFeature !== 'search'
		) {
			return
		}

		addUiBreadcrumb({
			category: 'search',
			message: 'search_home_navigation_checkpoint',
			data: {
				currentPath: location.pathname,
				searchRoute,
				tenantId,
				userId: profile?.id,
				username: profile?.username,
				tenantCount: Array.isArray(tenants) ? tenants.length : null,
				columnCount: columns.length,
				filterText,
				searchDiagnostics,
				dom: getDomSnapshot(),
				lastNavigation: navigation,
			},
		})
	}, [
		columns.length,
		filterText,
		location.pathname,
		location.search,
		profile?.id,
		profile?.username,
		searchDiagnostics,
		searchRoute,
		tenantId,
		tenants,
	])

	useEffect(() => {
		if (tenants?.length > 0) {
			try {
				const tenantSearchLS = getLS('tenantSearch_sort')
				const defaultColumns = allColumnsWithSelector.filter(col => col.defaultShow)
				let sortedColumns = defaultColumns

				if (tenantSearchLS) {
					sortedColumns = tenantSearchLS
						.map(key => {
							return allColumnsWithSelector.find(column => column.id === key)
						})
						.filter(Boolean)
				}

				if (sortedColumns.length === 0) {
					sortedColumns = defaultColumns
				}

				addUiBreadcrumb({
					category: 'search',
					message: 'search_restore_columns',
					data: {
						currentPath: location.pathname,
						searchRoute,
						tenantId,
						storedColumnIds: tenantSearchLS || [],
						restoredColumnCount: sortedColumns.length,
						lastNavigation: getNavigationSnapshot(),
					},
				})

				setColumns(sortedColumns)
			} catch (error) {
				captureFeatureException(error, {
					feature: 'search',
					location: 'Search.restoreColumns',
					extra: {
						currentPath: location.pathname,
						searchRoute,
						tenantId,
						userId: profile?.id,
						username: profile?.username,
						storedColumnIds: getLS('tenantSearch_sort'),
						searchDiagnostics,
						dom: getDomSnapshot(),
						lastNavigation: getNavigationSnapshot(),
					},
				})

				setColumns(allColumnsWithSelector.filter(col => col.defaultShow))
			}
		}
	}, [location.pathname, profile?.id, profile?.username, tenantId, tenants])

	useEffect(() => {
		if (tenants?.length > 0) {
			try {
				const filteredItems = filteredData(filterText, tenants) || []
				const finalItems = filteredItems.filter(
					tenant => !forbiddenTenantIds.includes(tenant.id)
				)

				if (!hasLoggedFilterInit.current) {
					hasLoggedFilterInit.current = true
					addUiBreadcrumb({
						category: 'search',
						message: 'search_filter_initialized',
						data: {
							currentPath: location.pathname,
							searchRoute,
							tenantId,
							tenantCount: tenants.length,
							filteredTenantCount: finalItems.length,
							filterText,
							systemJsonLoaded: Array.isArray(systemJson?.forbiddenTenantsInSearch),
							storedColumnIds: getLS('tenantSearch_sort') || [],
							searchDiagnostics,
							lastNavigation: getNavigationSnapshot(),
						},
					})
				}

				setDataTableData(finalItems)
			} catch (error) {
				captureFeatureException(error, {
					feature: 'search',
					location: 'Search.filterData',
					extra: {
						currentPath: location.pathname,
						searchRoute,
						tenantId,
						userId: profile?.id,
						username: profile?.username,
						tenantCount: tenants.length,
						filterText,
						systemJsonLoaded: Array.isArray(systemJson?.forbiddenTenantsInSearch),
						storedColumnIds: getLS('tenantSearch_sort') || [],
						searchDiagnostics,
						dom: getDomSnapshot(),
						lastNavigation: getNavigationSnapshot(),
					},
				})

				setDataTableData([])
			}
		} else if (tenants?.length === 0) {
			setDataTableData([])
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		filterText,
		forbiddenTenantIds,
		location.pathname,
		profile?.id,
		profile?.username,
		systemJson,
		tenantId,
		tenants,
	])

	const handleClear = () => {
		if (filterText) {
			addUiBreadcrumb({
				category: 'search',
				message: 'search_filter_cleared',
				data: {
					currentPath: location.pathname,
					searchRoute,
					tenantId,
					previousFilterText: filterText,
					lastNavigation: getNavigationSnapshot(),
				},
			})
			setResetPaginationToggle(!resetPaginationToggle)
			setFilterText('')
		}
	}

	const setFilterHandler = e => setFilterText(e.target.value)

	const handleExpandedInput = isExpanded => {
		addUiBreadcrumb({
			category: 'search',
			message: 'search_input_toggle',
			data: {
				currentPath: location.pathname,
				searchRoute,
				tenantId,
				nextExpandedState: isExpanded,
				lastNavigation: getNavigationSnapshot(),
			},
		})
	}

	return (
		<>
			<HeaderPage>
				<HeaderButtons
					text="Suche"
					onToggleInput={handleExpandedInput}
					toInput
					inputText={filterText}
					onInputChange={setFilterHandler}
					onInputClear={handleClear}
				>
					<FontAwesomeIcon icon={faSearch} className="w-5 h-5" />
				</HeaderButtons>
			</HeaderPage>
			<ContentPage>
				{dataTableData?.length > 0 && (
					<DataTableComponent columns={columns} data={dataTableData} />
				)}
				{dataTableData === null && (
					<SimulateTable columns={columns} rows={10} addBottomPage />
				)}
				{dataTableData?.length === 0 && (
					<EmptyTable>Es konnte keine Filiale gefunden werden!</EmptyTable>
				)}
			</ContentPage>
			{dataTableData?.length === 0 && <BottomPage />}
		</>
	)
}

export default Search
